import { type CSSProperties, type ReactNode } from 'react';
import { Badge, TechGrid } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { BrandMark, BrandWordmark } from './BrandMark';
import { OfflineBanner } from './OfflineBanner';
import { Sidebar } from './Sidebar';
import { MainContent } from './MainContent';

export interface ShellProps {
  children: ReactNode;
  fullBleed?: boolean;
  version?: string;
}

export function Shell({ children, fullBleed = false }: ShellProps) {
  // E14.F1.T04: Topbar removido das rotas autenticadas (decisão owner
  // 2026-05-22). Navegação fica só no Sidebar; logout migra para o fim do
  // Sidebar.
  return (
    // E54.F11.C02: abaixo de `md` a barra deixa de dividir a linha com o
    // conteúdo. Sem isso a coluna de trabalho ficava com 154px num aparelho de
    // 390px — a raiz única dos nove estouros que a varredura acusou — e o
    // documento não denunciava, porque quem rolava era a coluna. Mesmo desenho
    // que o portal já usava desde a E50.F2.
    <div className="flex min-h-screen flex-col bg-bg text-fg md:grid md:grid-cols-[236px_1fr]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:bg-accent-700 focus:px-3 focus:py-1.5 focus:text-fg-onInverse"
      >
        {ptBR.nav.skipToContent}
      </a>
      <Sidebar />
      <main className="flex min-w-0 flex-col">
        <OfflineBanner />
        {fullBleed ? (
          <section
            id="main-content"
            tabIndex={-1}
            className="flex-1 overflow-hidden bg-bg focus:outline-none"
          >
            {children}
          </section>
        ) : (
          <MainContent>{children}</MainContent>
        )}
      </main>
    </div>
  );
}

/**
 * Curvas de assinatura do hero da prancha 1A. Três traços em aço claro que se
 * desenham por `stroke-dashoffset` — o gesto que o produto analisa, usado como
 * fundo. O comprimento de cada traço vira `--sc-trace-length` para o `dasharray`
 * não estourar nem sobrar, e o atraso escalona a entrada.
 */
const TRACOS_DE_ASSINATURA = [
  {
    d: 'M20 330 C 130 120, 300 90, 420 250 C 520 385, 640 400, 760 250 C 820 175, 860 130, 890 100',
    stroke: 'oklch(var(--accent-300))',
    width: 9,
    length: 1500,
    delay: '0ms',
  },
  {
    d: 'M40 400 C 150 250, 330 210, 450 350 C 560 480, 690 470, 880 300',
    stroke: 'oklch(var(--accent-200))',
    width: 7,
    length: 1200,
    delay: '180ms',
  },
  {
    d: 'M0 250 C 160 180, 260 300, 400 300',
    stroke: 'oklch(var(--accent-200))',
    width: 2,
    length: 460,
    delay: '360ms',
  },
] as const;

function AssinaturasDoHero() {
  return (
    <svg
      viewBox="0 0 900 420"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
      data-testid="auth-hero-tracos"
      className="pointer-events-none absolute inset-x-0 bottom-0 h-[52%] w-full"
    >
      {TRACOS_DE_ASSINATURA.map((traco) => (
        <path
          key={traco.d}
          d={traco.d}
          fill="none"
          stroke={traco.stroke}
          strokeWidth={traco.width}
          strokeLinecap="round"
          data-sc-trace=""
          style={
            {
              '--sc-trace-length': traco.length,
              animationDelay: traco.delay,
            } as CSSProperties
          }
        />
      ))}
    </svg>
  );
}

/**
 * Porta de entrada da prancha 1A: split `1fr / 520px` — hero à esquerda sobre
 * grade técnica de 80px, coluna de acesso à direita sobre tinta a 3%. Abaixo de
 * `lg` o hero sai e sobra a coluna de acesso, que é a que trabalha.
 *
 * Quando `bare` é true (ex.: /onboarding) o split é suprimido e o conteúdo fica
 * em um wrapper centralizado mais largo.
 */
export function AuthShell({ children, bare = false }: { children: ReactNode; bare?: boolean }) {
  if (bare) {
    return (
      <div className="grid min-h-screen place-items-start bg-bg px-4 py-12">
        <div className="mx-auto w-full max-w-3xl">{children}</div>
      </div>
    );
  }
  return (
    <div
      className="grid min-h-screen bg-bg lg:grid-cols-[1fr_520px]"
      data-testid="auth-shell-split"
    >
      <TechGrid
        cell={80}
        aria-hidden="true"
        data-testid="auth-hero"
        className="hidden flex-col justify-between overflow-hidden border-r border-border px-14 py-11 text-fg lg:flex"
      >
        <AssinaturasDoHero />

        <div className="relative flex items-center gap-2.5">
          <BrandMark size={34} />
          <BrandWordmark eyebrow={ptBR.shell.brandKicker} />
        </div>

        <div className="relative flex max-w-[520px] flex-col gap-3.5">
          <p className="font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
            {ptBR.authShell.heroEyebrow}
          </p>
          <h2 className="text-balance font-display text-[44px] leading-none text-fg">
            {ptBR.authShell.heroTitle}
          </h2>
          <p className="max-w-sm text-body-md text-fg-muted">{ptBR.authShell.brandSubtitle}</p>
          <div className="flex flex-wrap gap-2 pt-1.5">
            <Badge variant="info">{ptBR.authShell.tagAcev}</Badge>
            <Badge variant="info">{ptBR.authShell.tagEnfsi}</Badge>
            <Badge variant="outline">{ptBR.authShell.tagCadeia}</Badge>
          </div>
        </div>
      </TechGrid>
      <main className="flex min-h-screen flex-col justify-center bg-fg/[0.03] px-6 py-12 lg:px-14">
        <div className="mx-auto w-full max-w-[408px]">{children}</div>
      </main>
    </div>
  );
}
